import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { HeroBlock } from '../../components/ai/HeroBlock';
import { CtaFooter } from '../../components/ai/CtaFooter';
import { getCMSPage, CMSPageContent } from '../../services/api';

/**
 * intrnauts.ai Home — manifesto shape per sketch 001 Variant B (Signal).
 * - Hero (HeroBlock): eyebrow + h1 with gradient-accent noun + lede + 2 CTAs
 * - Capability pills (hardcoded per CONTEXT.md — CMS gaps are hardcoded in v1)
 * - Pillars (3): "Three things we insist on" — CMS `features` blocks override
 *   the locked fallback copy when present (per RESEARCH.md Q3 mapping)
 * - CtaFooter
 *
 * No conversational widget in v1 (deferred to v1.1+, see motion-and-voice.md).
 */

interface Pillar {
  title: string;
  body: string;
}

const FALLBACK_PILLARS: Pillar[] = [
  {
    title: 'Innovation that ships.',
    body: "We measure novelty by whether the thing made it into someone's workflow, not by whether it got applause on demo day.",
  },
  {
    title: 'Engineering, not alchemy.',
    body: 'Evals, observability, retries, graceful degradation. The parts that turn a model into a product you can trust on call.',
  },
  {
    title: 'Community as discipline.',
    body: "We write down what we learn. Patterns, pitfalls, frameworks — shared so the next team doesn't have to relearn them.",
  },
];

const CAPABILITY_PILLS = [
  'Evals',
  'RAG that works',
  'Agentic systems',
  'Production observability',
  'Guardrails',
];

const HomeAi: React.FC = () => {
  const [content, setContent] = useState<CMSPageContent | null>(null);

  useEffect(() => {
    getCMSPage('home').then(res => {
      if (res.data) setContent(res.data);
    });
  }, []);

  const fields = content?.fields ?? {};
  const cmsPillars: Pillar[] = (content?.blocks?.features ?? [])
    .map(b => ({
      title: String(b.fields?.title ?? '').trim(),
      body: String(b.fields?.body ?? '').trim(),
    }))
    .filter(p => p.title || p.body);

  const pillars: Pillar[] = cmsPillars.length >= 3 ? cmsPillars.slice(0, 3) : FALLBACK_PILLARS;

  const heroHeadline = fields.hero_title
    ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fields.hero_title) }} />
    : (
      <>
        <span>What if AI</span>
        <span><i>earned</i></span>
        <span>its keep?</span>
      </>
    );

  const heroLede: React.ReactNode = fields.hero_tagline
    ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fields.hero_tagline) }} />
    : 'We build AI systems that survive contact with real work — eval-driven, observable, and designed for the people using them, not the slide deck selling them.';

  const primaryCtaLabel = fields.hero_cta_text || 'Book intro call';
  const primaryCtaTo = fields.hero_cta_url || '/contact';

  return (
    <>
      <HeroBlock
        eyebrow="● Home — building"
        headline={heroHeadline}
        lede={heroLede}
        ctas={[
          { label: primaryCtaLabel, to: primaryCtaTo, variant: 'magenta' },
          { label: 'See how we work', to: '/services', variant: 'outline' },
        ]}
        heroClassName="ai-home-hero"
      />

      <div className="ai-home-pills" aria-label="Capabilities">
        {CAPABILITY_PILLS.map(p => (
          <span key={p} className="ai-pill">{p}</span>
        ))}
      </div>

      <section className="ai-pillars" aria-labelledby="ai-pillars-head">
        <h2 id="ai-pillars-head" className="ai-pillars-head">
          Three things we <i>insist</i> on.
        </h2>
        <div className="ai-pillars-grid">
          {pillars.map((p, i) => (
            <article key={`${p.title}-${i}`} className="ai-pillar">
              <span className="ai-pillar-num">{`// 0${i + 1}`}</span>
              <h3 className="ai-pillar-title">{p.title}</h3>
              <div
                className="ai-pillar-body"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.body) }}
              />
            </article>
          ))}
        </div>
      </section>

      {fields.intro_text && (
        <section
          className="ai-home-intro"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fields.intro_text) }}
        />
      )}

      <CtaFooter headline={<>Try us with <i>something hard.</i></>} />
    </>
  );
};

export default HomeAi;
